import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Ticket,
  Search,
  Loader2,
  Plus,
  Edit,
  Eye,
  Trash2,
  Gift,
  TrendingUp,
  UserPlus,
  Percent,
  Calendar,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Copy,
  Check,
  Users,
  Clock,
  Infinity,
  Megaphone,
  AlertTriangle,
  X,
  BarChart3,
} from "lucide-react";
import PromoAnalyticsDashboard from "./PromoAnalyticsDashboard";

const CODE_TYPES = {
  single_use: { label: "Usage unique", icon: Ticket, color: "bg-blue-100 text-blue-800" },
  multi_uses_limit: { label: "Multi-usages (limite)", icon: Users, color: "bg-purple-100 text-purple-800" },
  multi_uses_expiry: { label: "Multi-usages (date)", icon: Calendar, color: "bg-amber-100 text-amber-800" },
  influencer: { label: "Influenceur", icon: Megaphone, color: "bg-pink-100 text-pink-800" },
};

const PromoCodeManagement = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [campaigns, setCampaigns] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [editingCode, setEditingCode] = useState(null);

  // Default form state used by both create and edit
  const defaultForm = {
    code: "",
    description: "",
    discount_rate: 25,
    code_type: "single_use",
    max_uses_per_user: 5,
    total_max_uses: 100,
    expires_at: "",
    // NEW campaign/event activation fields
    campaign_name: "",
    event_patterns_input: "", // comma-separated user input
    starts_at: "",
    applies_to: "all",
  };

  // Form states
  const [createForm, setCreateForm] = useState(defaultForm);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  // Assignment states
  const [userSearch, setUserSearch] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignNote, setAssignNote] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Fetch promo codes
  const fetchPromoCodes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("per_page", 20);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (typeFilter !== "all") params.append("code_type", typeFilter);
      if (campaignFilter !== "all") params.append("campaign_name", campaignFilter);

      const response = await axios.get(`${API}/admin/promo-codes?${params}`);
      setPromoCodes(response.data.promo_codes || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Erreur lors du chargement des codes promos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, campaignFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  // Fetch list of distinct campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes/campaigns`);
      setCampaigns(response.data?.campaigns || []);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  }, []);

  useEffect(() => {
    fetchPromoCodes();
    fetchStats();
    fetchCampaigns();
  }, [fetchPromoCodes, fetchStats, fetchCampaigns]);

  // Build the API payload from form state (shared by create/edit)
  const buildPayload = (form, isEdit = false) => {
    const payload = {
      description: form.description,
      discount_rate: parseFloat(form.discount_rate),
    };
    if (!isEdit) {
      payload.code_type = form.code_type;
      if (form.code) payload.code = form.code;
    }
    if (form.code_type === "multi_uses_limit") {
      payload.max_uses_per_user = parseInt(form.max_uses_per_user);
    }
    if (form.code_type === "multi_uses_expiry" || form.code_type === "influencer") {
      if (form.expires_at) {
        payload.expires_at = new Date(form.expires_at).toISOString();
      } else if (isEdit) {
        payload.expires_at = null;
      }
    }
    if (form.code_type === "influencer") {
      payload.total_max_uses = parseInt(form.total_max_uses);
    }
    // Campaign / event activation fields
    payload.campaign_name = form.campaign_name?.trim() || null;
    payload.event_name_patterns = form.event_patterns_input
      ? form.event_patterns_input.split(",").map((s) => s.trim()).filter(Boolean)
      : null;
    payload.starts_at = form.starts_at ? new Date(form.starts_at).toISOString() : null;
    payload.applies_to = form.applies_to || "all";
    return payload;
  };

  // Apply preset: "First transaction free" (shareable code, 100% off, first transaction only)
  const applyFirstTransactionPreset = () => {
    setCreateForm({
      ...defaultForm,
      code: "BIENVENUE",
      description: "Première transaction gratuite",
      discount_rate: 100,
      code_type: "influencer",
      total_max_uses: 1000,
      campaign_name: "Promotion d'accueil",
      applies_to: "first_transaction_only",
    });
    toast.info("Preset chargé — ajustez si besoin puis créez");
  };

  // Create promo code
  const handleCreate = async () => {
    if (!createForm.description) {
      toast.error("La description est requise");
      return;
    }

    // Validate type-specific fields
    if (createForm.code_type === "multi_uses_limit" && !createForm.max_uses_per_user) {
      toast.error("Le nombre maximum d'utilisations par membre est requis");
      return;
    }
    if (createForm.code_type === "multi_uses_expiry" && !createForm.expires_at) {
      toast.error("La date d'expiration est requise");
      return;
    }
    if (createForm.code_type === "influencer" && !createForm.total_max_uses) {
      toast.error("Le nombre total d'utilisations est requis");
      return;
    }

    setCreating(true);
    try {
      const payload = buildPayload(createForm, false);
      const response = await axios.post(`${API}/admin/promo-codes`, payload);
      toast.success(`Code promo "${response.data.code}" créé avec succès`);
      setShowCreateModal(false);
      setCreateForm(defaultForm);
      fetchPromoCodes();
      fetchStats();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  // Open edit modal pre-filled with code's current values
  const openEditModal = (code) => {
    setEditingCode(code);
    setCreateForm({
      code: code.code || "",
      description: code.description || "",
      discount_rate: code.discount_rate ?? 25,
      code_type: code.code_type || "single_use",
      max_uses_per_user: code.max_uses_per_user || 5,
      total_max_uses: code.total_max_uses || 100,
      expires_at: code.expires_at ? code.expires_at.split("T")[0] : "",
      campaign_name: code.campaign_name || "",
      event_patterns_input: Array.isArray(code.event_name_patterns)
        ? code.event_name_patterns.join(", ")
        : "",
      starts_at: code.starts_at ? code.starts_at.split("T")[0] : "",
      applies_to: code.applies_to || "all",
    });
    setShowEditModal(true);
  };

  // Save edits
  const handleEdit = async () => {
    if (!editingCode) return;
    if (!createForm.description) {
      toast.error("La description est requise");
      return;
    }
    setCreating(true);
    try {
      const payload = buildPayload(createForm, true);
      await axios.put(`${API}/admin/promo-codes/${editingCode.id}`, payload);
      toast.success("Code promo mis à jour");
      setShowEditModal(false);
      setEditingCode(null);
      setCreateForm(defaultForm);
      fetchPromoCodes();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setCreating(false);
    }
  };

  // Search users for assignment
  const searchUsers = async (query) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const response = await axios.get(`${API}/admin/promo-codes/search/users?q=${encodeURIComponent(query)}`);
      setUserSearchResults(response.data || []);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Assign code to user
  const handleAssign = async () => {
    if (!selectedUser || !selectedCode) {
      toast.error("Veuillez sélectionner un utilisateur");
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post(`${API}/admin/promo-codes/${selectedCode.id}/assign`, {
        user_email: selectedUser.email,
        note: assignNote,
      });
      toast.success(response.data.message);
      setShowAssignModal(false);
      setSelectedUser(null);
      setUserSearch("");
      setAssignNote("");
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'assignation");
    } finally {
      setAssigning(false);
    }
  };

  // Toggle active status
  const toggleActive = async (code) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${code.id}`, {
        is_active: !code.is_active,
      });
      toast.success(code.is_active ? "Code désactivé" : "Code activé");
      fetchPromoCodes();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Delete code
  const handleDelete = async (code) => {
    if (!confirm(`Supprimer le code "${code.code}"? Cette action est irréversible.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/promo-codes/${code.id}`);
      toast.success("Code supprimé");
      fetchPromoCodes();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  // Copy code
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Code copié!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCodeTypeInfo = (type) => {
    return CODE_TYPES[type] || CODE_TYPES.single_use;
  };

  // Filter codes by search
  const filteredCodes = promoCodes.filter((code) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      code.code.toLowerCase().includes(term) ||
      code.description.toLowerCase().includes(term) ||
      code.created_by?.toLowerCase().includes(term)
    );
  });

  const [activeTab, setActiveTab] = useState("codes");

  return (
    <div className="space-y-6">
      {/* Tabs for Codes vs Analytics */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="codes" className="flex items-center gap-2">
            <Gift className="h-4 w-4" />
            Codes Promos
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Rapports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="mt-6">
          <PromoAnalyticsDashboard />
        </TabsContent>

        <TabsContent value="codes" className="mt-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total codes</p>
                <p className="text-2xl font-bold text-brand-600">{stats?.total_codes || 0}</p>
              </div>
              <div className="p-3 bg-brand-100 rounded-full">
                <Gift className="h-6 w-6 text-brand-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Codes actifs</p>
                <p className="text-2xl font-bold text-green-600">{stats?.active_codes || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Check className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Assignés</p>
                <p className="text-2xl font-bold text-purple-600">{stats?.total_assigned || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <UserPlus className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Utilisés</p>
                <p className="text-2xl font-bold text-amber-600">{stats?.total_used || 0}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-[200px]"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="active">Actifs</SelectItem>
              <SelectItem value="expired">Expirés</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="single_use">Usage unique</SelectItem>
              <SelectItem value="multi_uses_limit">Multi-usages (limite)</SelectItem>
              <SelectItem value="multi_uses_expiry">Multi-usages (date)</SelectItem>
              <SelectItem value="influencer">Influenceur</SelectItem>
            </SelectContent>
          </Select>
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="w-[200px]" data-testid="campaign-filter">
              <SelectValue placeholder="Campagne" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes campagnes</SelectItem>
              {campaigns.map((c) => (
                <SelectItem key={c.campaign_name} value={c.campaign_name}>
                  {c.campaign_name} ({c.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { fetchPromoCodes(); fetchStats(); fetchCampaigns(); }}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Actualiser
          </Button>
          <Button
            variant="outline"
            onClick={() => { applyFirstTransactionPreset(); setShowCreateModal(true); }}
            className="border-pink-300 text-pink-700 hover:bg-pink-50"
            data-testid="welcome-promo-preset-btn"
          >
            <Gift className="h-4 w-4 mr-1" />
            Promo d'accueil
          </Button>
          <Button onClick={() => { setCreateForm(defaultForm); setShowCreateModal(true); }} className="bg-brand-600 hover:bg-brand-700" data-testid="new-promo-code-btn">
            <Plus className="h-4 w-4 mr-1" />
            Nouveau code
          </Button>
        </div>
      </div>

      {/* Promo Codes Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : filteredCodes.length === 0 ? (
            <div className="text-center py-12">
              <Gift className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Aucun code promo trouvé</p>
              <p className="text-sm text-gray-500 mt-1">Créez votre premier code promo</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-center">Réduction</TableHead>
                    <TableHead className="text-center">Assignés</TableHead>
                    <TableHead className="text-center">Utilisés</TableHead>
                    <TableHead>Créé par</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCodes.map((code) => {
                    const typeInfo = getCodeTypeInfo(code.code_type);
                    const TypeIcon = typeInfo.icon;
                    const isExpired = code.expires_at && new Date(code.expires_at) < new Date();
                    
                    return (
                      <TableRow key={code.id} className={!code.is_active || isExpired ? "opacity-60" : ""}>
                        <TableCell>
                          <div className="flex items-center gap-2 flex-wrap">
                            <code className="font-mono font-bold text-brand-600 bg-brand-50 px-2 py-1 rounded">
                              {code.code}
                            </code>
                            <button
                              onClick={() => copyCode(code.code)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              {copiedCode === code.code ? (
                                <Check className="h-4 w-4 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4 text-gray-400" />
                              )}
                            </button>
                            {!code.is_active && (
                              <Badge variant="secondary" className="bg-gray-100">Inactif</Badge>
                            )}
                            {isExpired && (
                              <Badge variant="destructive" className="bg-red-100 text-red-700">Expiré</Badge>
                            )}
                            {code.starts_at && new Date(code.starts_at) > new Date() && (
                              <Badge className="bg-indigo-100 text-indigo-700">
                                <Clock className="h-3 w-3 mr-1" />
                                Programmé
                              </Badge>
                            )}
                            {code.applies_to === "first_transaction_only" && (
                              <Badge className="bg-pink-100 text-pink-700">
                                <Gift className="h-3 w-3 mr-1" />
                                1ère transaction
                              </Badge>
                            )}
                            {Array.isArray(code.event_name_patterns) && code.event_name_patterns.length > 0 && (
                              <Badge className="bg-cyan-100 text-cyan-700" title={code.event_name_patterns.join(", ")}>
                                <Ticket className="h-3 w-3 mr-1" />
                                Événements
                              </Badge>
                            )}
                          </div>
                          {code.campaign_name && (
                            <div className="text-xs text-gray-500 mt-1 truncate" title={code.campaign_name}>
                              📁 {code.campaign_name}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={code.description}>
                          {code.description}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${typeInfo.color} font-normal`}>
                            <TypeIcon className="h-3 w-3 mr-1" />
                            {typeInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-semibold text-green-600">{code.discount_rate}%</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {code.times_assigned || 0}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={code.times_used > 0 ? "text-green-600 font-medium" : ""}>
                            {code.times_used || 0}
                          </span>
                          {code.code_type === "influencer" && code.total_max_uses && (
                            <span className="text-gray-400">/{code.total_max_uses}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {code.created_by?.split("@")[0]}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(code.created_at)}
                          {code.expires_at && (
                            <div className="text-xs text-amber-600">
                              Expire: {formatDate(code.expires_at)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedCode(code);
                                setShowDetailModal(true);
                              }}
                              title="Voir détails"
                              data-testid={`promo-detail-${code.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(code)}
                              title="Modifier"
                              data-testid={`promo-edit-${code.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {code.code_type !== "influencer" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedCode(code);
                                  setShowAssignModal(true);
                                }}
                                title="Assigner à un membre"
                                disabled={!code.is_active || isExpired}
                              >
                                <UserPlus className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActive(code)}
                              title={code.is_active ? "Désactiver" : "Activer"}
                            >
                              {code.is_active ? (
                                <X className="h-4 w-4 text-red-500" />
                              ) : (
                                <Check className="h-4 w-4 text-green-500" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(code)}
                              title="Supprimer"
                              disabled={code.times_used > 0}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {total} code{total > 1 ? "s" : ""} au total
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-brand-600" />
              Créer un code promo
            </DialogTitle>
            <DialogDescription>
              Générez un nouveau code promotionnel pour vos membres
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code (optionnel)</Label>
              <Input
                placeholder="Ex: NOEL2026 (auto-généré si vide)"
                value={createForm.code}
                onChange={(e) => setCreateForm({ ...createForm, code: e.target.value.toUpperCase() })}
              />
              <p className="text-xs text-gray-500">Laissez vide pour générer automatiquement</p>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                placeholder="Ex: Promotion de Noël 2026"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Réduction (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={createForm.discount_rate}
                  onChange={(e) => setCreateForm({ ...createForm, discount_rate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Type de code</Label>
                <Select
                  value={createForm.code_type}
                  onValueChange={(value) => setCreateForm({ ...createForm, code_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single_use">
                      <div className="flex items-center gap-2">
                        <Ticket className="h-4 w-4" />
                        Usage unique
                      </div>
                    </SelectItem>
                    <SelectItem value="multi_uses_limit">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Multi-usages (limite)
                      </div>
                    </SelectItem>
                    <SelectItem value="multi_uses_expiry">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Multi-usages (date)
                      </div>
                    </SelectItem>
                    <SelectItem value="influencer">
                      <div className="flex items-center gap-2">
                        <Megaphone className="h-4 w-4" />
                        Influenceur
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Type-specific fields */}
            {createForm.code_type === "multi_uses_limit" && (
              <div className="space-y-2 p-3 bg-purple-50 rounded-lg">
                <Label>Nombre max d'utilisations par membre</Label>
                <Input
                  type="number"
                  min="1"
                  value={createForm.max_uses_per_user}
                  onChange={(e) => setCreateForm({ ...createForm, max_uses_per_user: e.target.value })}
                />
                <p className="text-xs text-purple-600">Le membre pourra utiliser ce code X fois</p>
              </div>
            )}

            {createForm.code_type === "multi_uses_expiry" && (
              <div className="space-y-2 p-3 bg-amber-50 rounded-lg">
                <Label>Date d'expiration</Label>
                <Input
                  type="date"
                  value={createForm.expires_at}
                  onChange={(e) => setCreateForm({ ...createForm, expires_at: e.target.value })}
                  min={new Date().toISOString().split("T")[0]}
                />
                <p className="text-xs text-amber-600">Le membre pourra utiliser ce code jusqu'à cette date</p>
              </div>
            )}

            {createForm.code_type === "influencer" && (
              <div className="space-y-4 p-3 bg-pink-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Nombre total d'utilisations</Label>
                  <Input
                    type="number"
                    min="1"
                    value={createForm.total_max_uses}
                    onChange={(e) => setCreateForm({ ...createForm, total_max_uses: e.target.value })}
                  />
                  <p className="text-xs text-pink-600">Ce code pourra être utilisé X fois au total par n'importe qui</p>
                </div>
                <div className="space-y-2">
                  <Label>Date d'expiration (optionnel)</Label>
                  <Input
                    type="date"
                    value={createForm.expires_at}
                    onChange={(e) => setCreateForm({ ...createForm, expires_at: e.target.value })}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
              </div>
            )}

            {/* Type explanation */}
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              {createForm.code_type === "single_use" && (
                <p><strong>Usage unique:</strong> Le membre ne peut utiliser ce code qu'une seule fois. Après utilisation, le code disparaît de son dashboard.</p>
              )}
              {createForm.code_type === "multi_uses_limit" && (
                <p><strong>Multi-usages (limite):</strong> Le membre peut utiliser ce code un nombre limité de fois (ex: 5 utilisations).</p>
              )}
              {createForm.code_type === "multi_uses_expiry" && (
                <p><strong>Multi-usages (date):</strong> Le membre peut utiliser ce code autant de fois qu'il veut jusqu'à la date d'expiration.</p>
              )}
              {createForm.code_type === "influencer" && (
                <p><strong>Influenceur:</strong> Code partageable. N'importe qui peut l'utiliser jusqu'à atteindre le nombre total d'utilisations. Idéal pour les partenaires influenceurs.</p>
              )}
            </div>

            {/* ===== Campaign & Event Activation Section ===== */}
            <div className="space-y-4 p-3 border-2 border-dashed border-indigo-200 rounded-lg bg-indigo-50/40">
              <h4 className="font-semibold text-indigo-900 text-sm flex items-center gap-2">
                <Megaphone className="h-4 w-4" />
                Activation événementielle (optionnel)
              </h4>

              <div className="space-y-2">
                <Label>Nom de la campagne</Label>
                <Input
                  placeholder="Ex: Noël 2026, St-Valentin, Lancement"
                  value={createForm.campaign_name}
                  onChange={(e) => setCreateForm({ ...createForm, campaign_name: e.target.value })}
                  data-testid="campaign-name-input"
                />
                <p className="text-xs text-gray-500">Pour regrouper et filtrer vos codes par campagne</p>
              </div>

              <div className="space-y-2">
                <Label>Événements concernés (optionnel)</Label>
                <Input
                  placeholder="Ex: Osheaga, Taylor Swift, Canadiens"
                  value={createForm.event_patterns_input}
                  onChange={(e) => setCreateForm({ ...createForm, event_patterns_input: e.target.value })}
                  data-testid="event-patterns-input"
                />
                <p className="text-xs text-gray-500">Séparez par virgule. Le code ne s'appliquera qu'aux transactions dont le nom d'événement contient un de ces termes. Laissez vide pour tous événements.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date d'activation</Label>
                  <Input
                    type="date"
                    value={createForm.starts_at}
                    onChange={(e) => setCreateForm({ ...createForm, starts_at: e.target.value })}
                    data-testid="starts-at-input"
                  />
                  <p className="text-xs text-gray-500">Le code ne sera pas valide avant cette date</p>
                </div>
                {createForm.code_type !== "multi_uses_expiry" && (
                  <div className="space-y-2">
                    <Label>Date de fin</Label>
                    <Input
                      type="date"
                      value={createForm.expires_at}
                      onChange={(e) => setCreateForm({ ...createForm, expires_at: e.target.value })}
                      min={createForm.starts_at || new Date().toISOString().split("T")[0]}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Réservé aux nouveaux clients</Label>
                <Select
                  value={createForm.applies_to}
                  onValueChange={(value) => setCreateForm({ ...createForm, applies_to: value })}
                >
                  <SelectTrigger data-testid="applies-to-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les clients</SelectItem>
                    <SelectItem value="first_transaction_only">Première transaction uniquement</SelectItem>
                  </SelectContent>
                </Select>
                {createForm.applies_to === "first_transaction_only" && (
                  <p className="text-xs text-pink-600">
                    🎁 Code utilisable uniquement par les clients sans transaction complétée.
                    Avec une réduction de 100%, c'est l'idéal pour attirer de nouveaux clients !
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-brand-600 hover:bg-brand-700">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Créer le code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Modal */}
      <Dialog open={showAssignModal} onOpenChange={(open) => {
        setShowAssignModal(open);
        if (!open) {
          setSelectedUser(null);
          setUserSearch("");
          setUserSearchResults([]);
          setAssignNote("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-brand-600" />
              Assigner le code
            </DialogTitle>
            <DialogDescription>
              Attribuez le code <code className="font-mono font-bold text-brand-600">{selectedCode?.code}</code> à un membre
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rechercher un membre</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Email, nom ou numéro client..."
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Search results */}
            {userSearchResults.length > 0 && !selectedUser && (
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {userSearchResults.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => {
                      if (!user.has_active_promo_code) {
                        setSelectedUser(user);
                        setUserSearch(`${user.first_name} ${user.last_name} (${user.email})`);
                        setUserSearchResults([]);
                      }
                    }}
                    className={`p-3 hover:bg-gray-50 cursor-pointer border-b last:border-0 ${
                      user.has_active_promo_code ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{user.first_name} {user.last_name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <p className="text-xs text-gray-400">#{user.client_number}</p>
                      </div>
                      {user.has_active_promo_code && (
                        <Badge variant="secondary" className="text-xs">
                          A déjà un code
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected user */}
            {selectedUser && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-800">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </p>
                    <p className="text-sm text-green-600">{selectedUser.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedUser(null);
                      setUserSearch("");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Note (optionnel)</Label>
              <Textarea
                placeholder="Ex: Client fidèle, compensation pour problème..."
                value={assignNote}
                onChange={(e) => setAssignNote(e.target.value)}
                rows={2}
              />
            </div>

            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p>
                <strong>Info:</strong> Une fois assigné, le code apparaîtra dans le dashboard du membre. 
                Il devra le copier et l'entrer lors d'un transfert pour bénéficier de la réduction de{" "}
                <strong>{selectedCode?.discount_rate}%</strong>.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedUser || assigning}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {assigning ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-brand-600" />
              Détails du code promo
            </DialogTitle>
          </DialogHeader>

          {selectedCode && (
            <div className="space-y-6 py-4">
              {/* Code Info */}
              <div className="flex items-center justify-between p-4 bg-brand-50 rounded-lg">
                <div>
                  <code className="text-2xl font-mono font-bold text-brand-600">{selectedCode.code}</code>
                  <p className="text-gray-600 mt-1">{selectedCode.description}</p>
                </div>
                <div className="text-right">
                  <Badge className={getCodeTypeInfo(selectedCode.code_type).color}>
                    {getCodeTypeInfo(selectedCode.code_type).label}
                  </Badge>
                  <p className="text-2xl font-bold text-green-600 mt-1">{selectedCode.discount_rate}%</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-brand-600">{selectedCode.times_assigned || 0}</p>
                    <p className="text-sm text-gray-500">Assignés</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{selectedCode.times_used || 0}</p>
                    <p className="text-sm text-gray-500">Utilisés</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className={`text-3xl font-bold ${selectedCode.is_active ? "text-green-600" : "text-red-600"}`}>
                      {selectedCode.is_active ? "Actif" : "Inactif"}
                    </p>
                    <p className="text-sm text-gray-500">Statut</p>
                  </CardContent>
                </Card>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Créé par</p>
                  <p className="font-medium">{selectedCode.created_by}</p>
                </div>
                <div>
                  <p className="text-gray-500">Date de création</p>
                  <p className="font-medium">{formatDate(selectedCode.created_at)}</p>
                </div>
                {selectedCode.expires_at && (
                  <div>
                    <p className="text-gray-500">Expire le</p>
                    <p className="font-medium text-amber-600">{formatDate(selectedCode.expires_at)}</p>
                  </div>
                )}
                {selectedCode.max_uses_per_user && (
                  <div>
                    <p className="text-gray-500">Max par membre</p>
                    <p className="font-medium">{selectedCode.max_uses_per_user} utilisations</p>
                  </div>
                )}
                {selectedCode.total_max_uses && (
                  <div>
                    <p className="text-gray-500">Max total</p>
                    <p className="font-medium">{selectedCode.times_used || 0} / {selectedCode.total_max_uses}</p>
                  </div>
                )}
              </div>

              {/* Assignments */}
              {selectedCode.assignments && selectedCode.assignments.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Membres assignés ({selectedCode.assignments.length})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Membre</TableHead>
                          <TableHead>Code personnel</TableHead>
                          <TableHead className="text-center">Utilisations</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Par</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedCode.assignments.map((assignment, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{assignment.user_name || "-"}</p>
                                <p className="text-xs text-gray-500">{assignment.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                                {assignment.personal_code}
                              </code>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={assignment.times_used > 0 ? "text-green-600 font-medium" : ""}>
                                {assignment.times_used || 0}
                              </span>
                              {assignment.uses_remaining !== null && assignment.uses_remaining !== undefined && (
                                <span className="text-gray-400">/{assignment.uses_remaining + (assignment.times_used || 0)}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {formatDate(assignment.assigned_at)}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {assignment.assigned_by?.split("@")[0]}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Usage History */}
              {selectedCode.usage_history && selectedCode.usage_history.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Historique d'utilisation ({selectedCode.usage_history.length})</h4>
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {selectedCode.usage_history.map((usage, idx) => (
                      <div key={idx} className="p-3 border-b last:border-0 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">
                            {usage.personal_code || usage.code}
                          </span>
                          <span className="text-gray-500">{formatDate(usage.used_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => {
        setShowEditModal(open);
        if (!open) {
          setEditingCode(null);
          setCreateForm(defaultForm);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-brand-600" />
              Modifier le code promo
            </DialogTitle>
            <DialogDescription>
              {editingCode && (
                <span>Code: <code className="font-mono font-bold text-brand-600">{editingCode.code}</code></span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Réduction (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={createForm.discount_rate}
                  onChange={(e) => setCreateForm({ ...createForm, discount_rate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Type (non modifiable)</Label>
                <Input value={getCodeTypeInfo(createForm.code_type).label} disabled />
              </div>
            </div>

            {createForm.code_type === "multi_uses_limit" && (
              <div className="space-y-2">
                <Label>Nombre max par membre</Label>
                <Input
                  type="number"
                  min="1"
                  value={createForm.max_uses_per_user}
                  onChange={(e) => setCreateForm({ ...createForm, max_uses_per_user: e.target.value })}
                />
              </div>
            )}

            {createForm.code_type === "influencer" && (
              <div className="space-y-2">
                <Label>Nombre total d'utilisations</Label>
                <Input
                  type="number"
                  min="1"
                  value={createForm.total_max_uses}
                  onChange={(e) => setCreateForm({ ...createForm, total_max_uses: e.target.value })}
                />
              </div>
            )}

            <div className="space-y-4 p-3 border-2 border-dashed border-indigo-200 rounded-lg bg-indigo-50/40">
              <h4 className="font-semibold text-indigo-900 text-sm flex items-center gap-2">
                <Megaphone className="h-4 w-4" />
                Activation événementielle
              </h4>
              <div className="space-y-2">
                <Label>Nom de la campagne</Label>
                <Input
                  placeholder="Ex: Noël 2026"
                  value={createForm.campaign_name}
                  onChange={(e) => setCreateForm({ ...createForm, campaign_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Événements concernés</Label>
                <Input
                  placeholder="Ex: Osheaga, Taylor Swift (séparés par virgule)"
                  value={createForm.event_patterns_input}
                  onChange={(e) => setCreateForm({ ...createForm, event_patterns_input: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date d'activation</Label>
                  <Input
                    type="date"
                    value={createForm.starts_at}
                    onChange={(e) => setCreateForm({ ...createForm, starts_at: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date d'expiration</Label>
                  <Input
                    type="date"
                    value={createForm.expires_at}
                    onChange={(e) => setCreateForm({ ...createForm, expires_at: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cible</Label>
                <Select
                  value={createForm.applies_to}
                  onValueChange={(value) => setCreateForm({ ...createForm, applies_to: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les clients</SelectItem>
                    <SelectItem value="first_transaction_only">Première transaction uniquement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleEdit} disabled={creating} className="bg-brand-600 hover:bg-brand-700" data-testid="save-edit-promo-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PromoCodeManagement;
